import type { LocalFormItem, LocalFormItemData, LocalFormItemTab } from 'types/agendaForm.ts';
import type { UpdateSectionActionInputs } from 'types';
import { createRandomString } from 'utils/common';
import { getRange } from './gridUtils';

export const generateGridItems = (items: LocalFormItem[], sizeX: number, sizeY: number): LocalFormItem[] => {
  const itemMap = new Map<string, LocalFormItem>(
    items
      .filter((i) => !i.isEmpty && i.x && i.y)
      .map((item) => {
        const { start: startX } = getRange(item.x!);
        const { start: startY } = getRange(item.y!);
        return [`${startX},${startY}`, item];
      })
  );

  return Array.from({ length: sizeX * sizeY }, (_, index) => {
    const x = index % sizeX;
    const y = Math.floor(index / sizeX);
    const key = `${x},${y}`;

    const existingItem = itemMap.get(key);

    return existingItem
      ? { ...existingItem }
      : {
          x: String(x),
          y: String(y),
          identifier: createRandomString(10),
          isEmpty: true,
          data: null,
        };
  });
};

export const recalcSectionItems = (formItemTabs: LocalFormItemTab[]): LocalFormItem[] => {
  const allItems: LocalFormItem[] = [];

  formItemTabs.forEach((tab) => allItems.push(...tab.items));

  return allItems;
};

export const findItemIndex = (items: LocalFormItem[], itemId: string) => {
  return items.findIndex((item) => item.data?.name === itemId);
};

export const findItemIndexByCoordinate = (items: LocalFormItem[], targetX: number, targetY: number) => {
  return items.findIndex((item) => {
    const xRange = getRange(item.x);
    const yRange = getRange(item.y);
    return targetX >= xRange.start && targetX <= xRange.end && targetY >= yRange.start && targetY <= yRange.end;
  });
};

export const omitNonActionInputs = (items: LocalFormItem[]) =>
  items.filter((item): item is LocalFormItem & { data: LocalFormItemData } => {
    if (!item.data) return false;

    const itemType = item.data.definition.type;
    return itemType !== 'Button' && itemType !== 'AutomaticSequence' && itemType !== 'Computed';
  });

export const setAllActionsInputs = (formItemTabs: LocalFormItemTab[], value: boolean) => {
  formItemTabs.forEach((tab) => {
    const validItems = omitNonActionInputs(tab.items);
    validItems.forEach((item) => (item.data.isActionInput = value));
  });
};

export const getNextActionInputsState = (
  isChecked: boolean,
  currentState: UpdateSectionActionInputs,
  hasSomeInputs: boolean
): UpdateSectionActionInputs => {
  if (isChecked) {
    if (currentState === 'none' && hasSomeInputs) {
      return 'indeterminate';
    }
    return 'all';
  }

  if (currentState === 'indeterminate') {
    return 'all';
  }

  return 'none';
};
